import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../hooks/useAuth';
import { useBlogPosts } from '../../../hooks/useBlogPosts';
import BlogPostList from '../../../components/blog/BlogPostList';
import { PenTool, Eye, Calendar, BarChart3, FileText, Wand2, Loader2 } from 'lucide-react';

const BlogDashboard = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth(); // Fix: get user as currentUser
  const { 
    posts, 
    categories, 
    loading, 
    error, 
    fetchPosts,
    fetchStats,
    deletePost,
    clearError 
  } = useBlogPosts();

  const [stats, setStats] = useState({
    totalPosts: 0,
    draftPosts: 0,
    publishedPosts: 0,
    scheduledPosts: 0
  });

  // Load posts on component mount
  useEffect(() => {
    loadPosts();
    loadStats();
  }, []);

  // Calculate stats from live server count — not from the paginated posts array
  const loadStats = async () => {
    try {
      const counts = await fetchStats();
      setStats(counts);
    } catch (err) {
      console.error('Error loading blog stats:', err);
    }
  };

  const loadPosts = async () => {
    try {
      await fetchPosts({
        limit: 20,
        orderBy: 'updatedAt',
        orderDirection: 'desc'
      });
    } catch (err) {
      console.error('Error loading posts:', err);
    }
  };

  const [generatingBlog, setGeneratingBlog] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);

  const handleGenerateBlogPost = async () => {
    if (!window.confirm('Generate a new AI draft blog post? It will appear in your drafts list — you can review and edit before publishing.')) return;
    setGeneratingBlog(true);
    try {
      const fns = getFunctions();
      const fn = httpsCallable(fns, 'triggerBlogGeneration');
      const result = await fn();
      setLastGenerated(result.data?.title || 'New post');
      await loadPosts();
      await loadStats();
    } catch (err) {
      console.error('Blog generation error:', err);
      alert(`Generation failed: ${err.message}`);
    } finally {
      setGeneratingBlog(false);
    }
  };

  const handleCreateNew = () => {
    navigate('/admin/blog/posts/new');
  };

  const handleEdit = (post) => {
    navigate(`/admin/blog/posts/edit/${post.id}`);
  };

  const handleView = (post) => {
    if (post.status === 'published') {
      window.open(`/blog/${post.slug}`, '_blank');
    } else {
      navigate(`/admin/blog/posts/preview/${post.id}`);
    }
  };

  const handleDelete = async (postId) => {
    try {
      await deletePost(postId);
      await loadStats();
    } catch (err) {
      console.error('Error deleting post:', err);
    }
  };

  const handleLoadMore = async () => {
    try {
      const lastPost = posts[posts.length - 1];
      await fetchPosts({
        limit: 20,
        startAfter: lastPost,
        orderBy: 'updatedAt',
        orderDirection: 'desc'
      });
    } catch (err) {
      console.error('Error loading more posts:', err);
    }
  };

  const statCards = [
    {
      title: 'Total Posts',
      value: stats.totalPosts,
      icon: PenTool,
      color: 'purple'
    },
    {
      title: 'Published',
      value: stats.publishedPosts,
      icon: Eye,
      color: 'green'
    },
    {
      title: 'Drafts',
      value: stats.draftPosts,
      icon: BarChart3,
      color: 'gray'
    },
    {
      title: 'Scheduled',
      value: stats.scheduledPosts,
      icon: Calendar,
      color: 'blue'
    }
  ];

  return (
    <div className="blog-dashboard min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Blog Management</h1>
          <p className="text-gray-600 mt-2">
            Create and manage your blog content to engage with your dental hygienist community.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center">
                <div className={`p-2 rounded-lg bg-${stat.color}-100`}>
                  <stat.icon 
                    size={24} 
                    className={`text-${stat.color}-600`}
                  />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    {stat.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stat.value}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* AI Blog Generator banner */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Wand2 size={18} className="text-purple-600" />
              <h2 className="font-semibold text-gray-900">AI Blog Generator</h2>
            </div>
            <p className="text-sm text-gray-600">
              Generate a new draft blog post written for UK dental hygienists. Posts are saved as drafts — review, edit, then publish.
              Auto-generates every 3 days in the background.
            </p>
            {lastGenerated && (
              <p className="text-xs text-purple-700 mt-1 font-medium">
                ✓ Just created: &ldquo;{lastGenerated}&rdquo; — it&apos;s in your drafts below.
              </p>
            )}
          </div>
          <button
            onClick={handleGenerateBlogPost}
            disabled={generatingBlog}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm text-white transition-opacity shrink-0"
            style={{
              background: generatingBlog ? '#9ca3af' : 'linear-gradient(135deg,#7c3aed,#6366f1)',
              cursor: generatingBlog ? 'default' : 'pointer',
              opacity: generatingBlog ? 0.8 : 1,
            }}
          >
            {generatingBlog
              ? <><Loader2 size={16} className="animate-spin" /> Generating…</>
              : <><Wand2 size={16} /> Generate Draft Post</>
            }
          </button>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={handleCreateNew}
              className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <PenTool size={20} className="text-purple-600 mb-2" />
              <h3 className="font-medium text-gray-900">Create New Post</h3>
              <p className="text-sm text-gray-600">Start writing a new blog post</p>
            </button>
            
            <button
              onClick={() => navigate('/admin/blog/categories')}
              className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <BarChart3 size={20} className="text-green-600 mb-2" />
              <h3 className="font-medium text-gray-900">Manage Categories</h3>
              <p className="text-sm text-gray-600">Organize your content topics</p>
            </button>
            
            <button
              onClick={() => navigate('/admin/blog/sitemap-generator')}
              className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <FileText size={20} className="text-orange-600 mb-2" />
              <h3 className="font-medium text-gray-900">Generate Sitemap</h3>
              <p className="text-sm text-gray-600">Create XML sitemap with all posts</p>
            </button>
            
            <button
              onClick={() => navigate('/blog')}
              className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <Eye size={20} className="text-blue-600 mb-2" />
              <h3 className="font-medium text-gray-900">View Public Blog</h3>
              <p className="text-sm text-gray-600">See your blog as visitors do</p>
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading blog data
                </h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <div className="mt-4">
                  <button
                    onClick={() => {
                      clearError();
                      loadPosts();
                    }}
                    className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded-md hover:bg-red-200 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Posts List */}
        <BlogPostList
          posts={posts}
          categories={categories}
          loading={loading}
          onCreateNew={handleCreateNew}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onLoadMore={handleLoadMore}
          hasMore={posts.length >= 20} // Simple check, could be improved
        />
      </div>
    </div>
  );
};

export default BlogDashboard;
